<template>
  <div class="scorm-player" :style="{ minHeight: iframeHeight }">
    <div v-if="loading" class="scorm-player__overlay">
      <div class="scorm-player__loader-content text-center">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="sr-only">{{ $t('page.general.cargar') }}</span>
        </div>
        <p class="scorm-player__loader-text mb-0">Se está cargando el contenido...</p>
      </div>
    </div>
    <div v-if="error" class="alert alert-danger m-3">{{ error }}</div>
    <iframe
      v-if="launchUrl && !error"
      ref="scormFrame"
      :src="launchUrl"
      class="scorm-player__iframe"
      :class="{ 'scorm-player__iframe--loading': loading }"
      :style="{ height: iframeHeight }"
      title="SCORM"
      @load="onIframeLoad"
    />
  </div>
</template>

<script>
import { createRuntimeBridge, attachApiBridge, detachApiBridge } from '@scorm/runtime/createRuntimeBridge';
import { resolvePublicAssetPath, applyFreshAttemptProgress, detectUnsafeStorylineFromLaunch } from '@scorm/runtime/utils';
import {
  initializeAttempt,
  commitProgress,
  terminateAttempt,
  fetchByTopic,
  buildRuntimePayload,
} from '@scorm/services/scormApiClient';

export default {
  name: 'ScormPlayer',
  props: {
    cveTema: { type: [String, Number], required: true },
    iframeHeight: { type: String, default: '45em' },
  },
  data() {
    return {
      loading: true,
      error: '',
      launchUrl: '',
      scormPackage: null,
      progress: null,
      bridge: null,
      initialized: false,
      contentReady: false,
      loadFallbackTimer: null,
    };
  },
  async mounted() {
    await this.bootstrap();
  },
  beforeDestroy() {
    this.clearLoadFallback();
    this.teardown(true);
  },
  methods: {
    async bootstrap() {
      try {
        const topicData = await fetchByTopic(this.cveTema);
        if (!topicData.success || !topicData.scormPackage) {
          throw new Error('No se encontró paquete SCORM para este tema');
        }
        this.scormPackage = topicData.scormPackage;
        const init = await initializeAttempt({
          courseId: this.cveTema,
          scormPackageId: this.scormPackage.id,
          version: this.scormPackage.version,
        });
        if (!init.success) {
          throw new Error(init.message || 'No se pudo inicializar SCORM');
        }
        this.progress = init.progress;
        this.launchUrl = resolvePublicAssetPath(this.scormPackage.launchUrl);
        const unsafeStoryline = await detectUnsafeStorylineFromLaunch(this.launchUrl);
        if (unsafeStoryline) {
          this.progress = applyFreshAttemptProgress(this.progress);
        }
        this.setupBridge();
        this.startLoadFallback();
      } catch (e) {
        this.error = e.message || 'Error al cargar SCORM';
        this.loading = false;
        this.$emit('error', this.error);
      }
    },
    setupBridge() {
      const version = this.scormPackage.version || '1.2';
      const courseId = this.cveTema;
      const scormPackageId = this.scormPackage.id;
      const onCommit = (body) => {
        commitProgress(
          buildRuntimePayload(courseId, scormPackageId, version, body)
        ).catch(() => {});
      };
      const onTerminate = (body) => {
        terminateAttempt(
          buildRuntimePayload(courseId, scormPackageId, version, body)
        )
          .catch(() => {})
          .finally(() => this.$emit('exit'));
      };
      this.bridge = createRuntimeBridge({ version, onCommit, onTerminate });
      this.bridge.runtime.hydrateFromProgress(this.progress);
      attachApiBridge(this.bridge.adapter, version, null);
    },
    onIframeLoad() {
      if (!this.contentReady) {
        this.markContentReady();
      }
      if (!this.bridge || this.initialized) return;
      const iframe = this.$refs.scormFrame;
      if (!iframe) return;
      try {
        attachApiBridge(this.bridge.adapter, this.scormPackage.version, iframe.contentWindow);
        this.initialized = true;
      } catch (e) {
        console.warn('[ScormPlayer] iframe API attach', e);
      }
    },
    teardown(terminate) {
      if (
        this.bridge &&
        terminate &&
        this.initialized &&
        this.bridge.runtime &&
        !this.bridge.runtime.terminated
      ) {
        try {
          const body = this.bridge.runtime.buildCommitPayload();
          terminateAttempt(
            buildRuntimePayload(
              this.cveTema,
              this.scormPackage.id,
              this.scormPackage.version,
              body
            )
          ).catch(() => {});
        } catch (e) {
          /* ignore */
        }
      }
      detachApiBridge();
      this.bridge = null;
    },
    startLoadFallback() {
      this.clearLoadFallback();
      this.loadFallbackTimer = setTimeout(() => {
        if (this.loading) {
          this.markContentReady();
        }
      }, 20000);
    },
    clearLoadFallback() {
      if (this.loadFallbackTimer) {
        clearTimeout(this.loadFallbackTimer);
        this.loadFallbackTimer = null;
      }
    },
    markContentReady() {
      if (this.contentReady) return;
      this.contentReady = true;
      this.clearLoadFallback();
      this.loading = false;
      this.$emit('ready');
    },
  },
};
</script>

<style scoped>
.scorm-player {
  position: relative;
  width: 100%;
}
.scorm-player__iframe {
  width: 100%;
  border: 0;
  display: block;
}
.scorm-player__iframe--loading {
  visibility: hidden;
}
.scorm-player__overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.92);
}
.scorm-player__loader-text {
  color: #495057;
  font-size: 1rem;
}
</style>



// WEBPACK FOOTER //
// scorm/frontend/components/ScormPlayer.vue